import * as React from "react";
import { useIntl, defineMessages } from "react-intl";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";

/**
 * Figma Node IDs:
 * Design Frame: 2155:22116
 * Instance IDs: I2081:3005;74:2277, I2081:2997;74:2277, I2081:2990;74:2277, I2081:2983;74:2277, I2081:2976;74:2277, I2081:2969;74:2277
 * Composite: true
 * Last Updated: 2024-12-31
 * Generated by: Claude Code figma-component command
 */

import { Plus } from "@labxchange/icons";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { fetchTranslations } from "./locales/config";
import * as styles from "./TextArea.css";

const messages = defineMessages({
  characterLimit: {
    id: "textarea.characterLimit",
    defaultMessage: "Character limit: {current}/{max}",
    description:
      "Shows the current character count and maximum allowed characters",
  },
});

export type TextAreaState = "default" | "success" | "error";

export interface TextAreaProps {
  id?: string;
  name?: string;
  value?: string;
  label?: string;
  placeholder?: string;
  helpText?: string;
  state?: TextAreaState;
  disabled?: boolean;
  showIcon?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  rows?: number;
  maxLength?: number;
  required?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  className?: string;
  testId?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  showCharacterLimit?: boolean;
}

const TextAreaInternal = ({
  id,
  name,
  value = "",
  label,
  placeholder,
  helpText,
  state = "default",
  disabled = false,
  showIcon = false,
  icon: Icon = Plus,
  rows = 4,
  maxLength,
  required = false,
  ariaLabel,
  ariaDescribedBy,
  className,
  testId,
  onChange,
  onFocus,
  onBlur,
  showCharacterLimit = false,
}: TextAreaProps) => {
  const intl = useIntl();
  const [isFocused, setIsFocused] = React.useState(false);
  const textareaId = id || React.useId();
  const helpTextId = `${textareaId}-help`;
  const characterCountId = `${textareaId}-character-count`;

  // Calculate character count
  const currentCharacterCount = value.length;
  const shouldShowCharacterLimit = showCharacterLimit && maxLength && !helpText;

  // Translate placeholder text
  const translatedPlaceholder = placeholder
    ? intl.formatMessage({ id: placeholder, defaultMessage: placeholder })
    : undefined;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (disabled) return;
    onChange?.(event.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const wrapperClassName = [styles.textAreaWrapper, className]
    .filter(Boolean)
    .join(" ");

  const labelClassName = [styles.label, disabled && styles.labelDisabled]
    .filter(Boolean)
    .join(" ");

  const containerClassName = [
    styles.container,
    isFocused && !disabled && state === "default" && styles.containerFocused,
    state === "success" && styles.containerSuccess,
    state === "error" && styles.containerError,
    disabled && styles.containerDisabled,
  ]
    .filter(Boolean)
    .join(" ");

  const iconClassName = [
    styles.icon,
    disabled && styles.iconDisabled,
    state === "success" && styles.iconSuccess,
    state === "error" && styles.iconError,
  ]
    .filter(Boolean)
    .join(" ");

  const textareaClassName = [
    styles.textareaElement,
    state === "success" && styles.textareaSuccess,
    state === "error" && styles.textareaError,
  ]
    .filter(Boolean)
    .join(" ");

  const helpTextClassName = [
    styles.helpText,
    disabled && styles.helpTextDisabled,
    state === "error" && styles.helpTextError,
  ]
    .filter(Boolean)
    .join(" ");

  const characterCountClassName = [
    styles.characterCount,
    disabled && styles.characterCountDisabled,
    state === "error" && styles.characterCountError,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClassName} data-testid={testId}>
      {label && (
        <label htmlFor={textareaId} className={labelClassName}>
          <RichText value={label} />
        </label>
      )}

      <div className={containerClassName}>
        {showIcon && (
          <div className={styles.iconContainer}>
            <Icon className={iconClassName} />
          </div>
        )}

        <textarea
          id={textareaId}
          name={name}
          value={value}
          placeholder={translatedPlaceholder}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          required={required}
          aria-label={ariaLabel}
          aria-describedby={
            helpText
              ? helpTextId
              : shouldShowCharacterLimit
                ? characterCountId
                : ariaDescribedBy
          }
          aria-invalid={state === "error"}
          className={textareaClassName}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>

      {helpText && (
        <p id={helpTextId} className={helpTextClassName}>
          <RichText value={helpText} />
        </p>
      )}

      {shouldShowCharacterLimit && (
        <p id={characterCountId} className={characterCountClassName}>
          {intl.formatMessage(messages.characterLimit, {
            current: currentCharacterCount,
            max: maxLength,
          })}
        </p>
      )}
    </div>
  );
};

export const TextArea = (props: TextAreaProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <TextAreaInternal {...props} />
  </TranslationsProvider>
);
